import { useEffect, useState, type FormEvent } from 'react';
import type { InstitutionSettings, UpdateInstitutionSettingsRequest } from '@app/shared';
import { AdminLayout } from '../components/admin-layout';
import { Alert, Button, Card, Input } from '../components/ui';
import {
  getInstitutionSettings,
  saveInstitutionSettings,
  uploadInstitutionLogo,
} from '../features/settings/api';
import { InstitutionLogo, clearInstitutionBrandCache } from '../components/institution-brand';

type Form = Record<
  'nama_yayasan' | 'nama_pesantren' | 'logo_url' | 'alamat' | 'nomor_telepon' | 'email' | 'website',
  string
>;
const blank: Form = {
  nama_yayasan: '',
  nama_pesantren: '',
  logo_url: '',
  alamat: '',
  nomor_telepon: '',
  email: '',
  website: '',
};

function toForm(data: InstitutionSettings): Form {
  return Object.fromEntries(
    Object.keys(blank).map((key) => [key, data[key as keyof Form] ?? '']),
  ) as Form;
}

export function InstitutionSettingsPage() {
  const [form, setForm] = useState<Form>(blank);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const uploadLogo = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    setError(null);
    setMessage(null);
    try {
      await uploadInstitutionLogo(file);
      clearInstitutionBrandCache(); // Clear cache agar komponen lain dapat data terbaru
      setMessage('Logo berhasil diunggah dan cache diperbarui.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal mengunggah logo');
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    getInstitutionSettings()
      .then((data) => {
        setForm(toForm(data));
        setError(null);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Gagal memuat identitas'))
      .finally(() => setLoading(false));
  }, []);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setMessage(null);
    const nullable = (value: string) => value.trim() || null;
    const body: UpdateInstitutionSettingsRequest = {
      nama_yayasan: nullable(form.nama_yayasan),
      nama_pesantren: form.nama_pesantren.trim(),
      logo_url: nullable(form.logo_url),
      alamat: form.alamat.trim(),
      nomor_telepon: form.nomor_telepon.trim(),
      email: form.email.trim(),
      website: nullable(form.website),
    };
    try {
      const saved = await saveInstitutionSettings(body);
      setForm(toForm(saved));
      clearInstitutionBrandCache(); // Clear cache agar komponen lain dapat data terbaru
      setMessage('Identitas lembaga berhasil disimpan dan cache diperbarui.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal menyimpan identitas');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout title="Identitas Lembaga">
      {error && <Alert type="error">{error}</Alert>}
      {message && <Alert type="success">{message}</Alert>}
      <Card style={{ maxWidth: 900 }}>
        {loading ? (
          <p>Memuat identitas…</p>
        ) : (
          <form onSubmit={submit}>
            <div
              style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}
            >
              <InstitutionLogo size={72} />
              <div>
                <strong>Pratinjau Logo</strong>
                <div style={{ color: '#64748B', fontSize: '.875rem' }}>
                  Fallback otomatis tersedia jika URL kosong/rusak.
                </div>
                <div
                  style={{
                    marginTop: '.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '.5rem',
                  }}
                >
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
                    aria-label="Unggah file logo"
                    disabled={uploading}
                    onChange={(e) => {
                      void uploadLogo(e.target.files?.[0]);
                      e.target.value = '';
                    }}
                    style={{ fontSize: '.875rem' }}
                  />
                  {uploading && (
                    <span style={{ color: '#64748B', fontSize: '.875rem' }}>Mengunggah…</span>
                  )}
                </div>
              </div>
            </div>
            <div className="form-grid">
              <Input
                label="Nama Yayasan (opsional)"
                name="nama_yayasan"
                value={form.nama_yayasan}
                onChange={(e) => setForm({ ...form, nama_yayasan: e.target.value })}
              />
              <Input
                label="Nama Pesantren"
                name="nama_pesantren"
                value={form.nama_pesantren}
                onChange={(e) => setForm({ ...form, nama_pesantren: e.target.value })}
                required
              />
              <Input
                label="Logo Pesantren (URL)"
                name="logo_url"
                type="url"
                value={form.logo_url}
                onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                placeholder="https://…"
              />
              <Input
                label="Nomor Telepon"
                name="nomor_telepon"
                value={form.nomor_telepon}
                onChange={(e) => setForm({ ...form, nomor_telepon: e.target.value })}
                required
              />
              <Input
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
              />
              <Input
                label="Website (opsional)"
                name="website"
                type="url"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                placeholder="https://…"
              />
            </div>
            <Input
              label="Alamat"
              name="alamat"
              value={form.alamat}
              onChange={(e) => setForm({ ...form, alamat: e.target.value })}
              required
            />
            <Button type="submit" disabled={saving}>
              {saving ? 'Menyimpan…' : 'Simpan Identitas'}
            </Button>
          </form>
        )}
      </Card>
    </AdminLayout>
  );
}
